import { Download, Eye, Trash2 } from 'lucide-react';
import React, { memo } from 'react';
import { ProjectPhoto } from '../../types';

interface PhotoCardProps {
  photo: ProjectPhoto;
  isSelected: boolean;
  categoryInfo: { value: string; label: string; color: string };
  permissions: any;
  onPhotoClick: (photo: ProjectPhoto) => void;
  onSelectionToggle: (photoId: string) => void;
  onDownload: (photo: ProjectPhoto) => void;
  onDelete: (photo: ProjectPhoto) => void;
  formatDate: (dateString: string) => string;
}

export const PhotoCard: React.FC<PhotoCardProps> = memo(
  ({
    photo,
    isSelected,
    categoryInfo,
    permissions,
    onPhotoClick,
    onSelectionToggle,
    onDownload,
    onDelete,
    formatDate,
  }) => {
    return (
      <div
        className={`relative group rounded-lg overflow-hidden bg-gray-50 border transition-all duration-200 hover:shadow-md ${
          isSelected
            ? 'ring-2 ring-brand-500 border-brand-500'
            : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        {/* Image */}
        <div className="aspect-square relative">
          <img
            src={photo.thumbnail_url || photo.file_url}
            alt={photo.title}
            className="w-full h-full object-cover cursor-pointer"
            onClick={() => onPhotoClick(photo)}
            loading="lazy"
            decoding="async"
            onError={e => {
              if (photo.thumbnail_url && e.currentTarget.src === photo.thumbnail_url) {
                e.currentTarget.src = photo.file_url;
              }
            }}
          />

          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Selection Checkbox */}
            <div className="absolute top-2 left-2">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelectionToggle(photo.photo_id)}
                className="w-4 h-4 rounded border-white/80 bg-white/80 text-brand-600 focus:ring-brand-500"
              />
            </div>

            {/* Category Badge */}
            <div className="absolute top-2 right-2">
              <span
                className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${categoryInfo.color}`}
              >
                {categoryInfo.label}
              </span>
            </div>

            {/* Quick Actions */}
            <div className="absolute bottom-2 right-2 flex gap-1">
              <button
                onClick={e => {
                  e.stopPropagation();
                  onPhotoClick(photo);
                }}
                className="p-1.5 bg-white/90 rounded hover:bg-white transition-colors"
                title="View"
              >
                <Eye className="h-3.5 w-3.5 text-gray-700" />
              </button>
              {permissions.download && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    onDownload(photo);
                  }}
                  className="p-1.5 bg-white/90 rounded hover:bg-white transition-colors"
                  title="Download"
                >
                  <Download className="h-3.5 w-3.5 text-brand-600" />
                </button>
              )}
              {permissions.delete && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    onDelete(photo);
                  }}
                  className="p-1.5 bg-white/90 rounded hover:bg-white transition-colors"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-600" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="p-2">
          <h3 className="text-xs font-medium text-gray-900 truncate" title={photo.title}>
            {photo.title}
          </h3>
          <p className="text-[10px] text-gray-500 mt-0.5">{formatDate(photo.uploaded_at)}</p>
        </div>
      </div>
    );
  }
);

PhotoCard.displayName = 'PhotoCard';
